// src/services/pairing.js
//
// 🟢 DEVICE PAIRING — WhatsApp-Web-style QR link.
// Flow (compatible with the existing mobile scanner):
//   1. web creates a session doc  web_auth_sessions/{sessionId}
//   2. web renders a QR encoding { sessionId, publicKey, deviceId } (the phone reads it)
//   3. phone verifies the logged-in user, mints a custom auth token, and writes it back
//      to the session doc (+ can register the web device itself); web signs in with it
//   4. web ALSO self-registers its public key under the user (belt & suspenders) and
//      cleans up the session doc.
//
// The QR now carries the web device's PUBLIC KEY so the phone can register it immediately
// (older phones that only return an authToken still work — web self-registers on sign-in).

import { doc, setDoc, onSnapshot, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { getIdentity, registerDevice } from '../crypto/identity';
import { decryptText, getDeviceKeys } from '../crypto/e2ee';
import { webLoginPayload } from './pairingKey';
import { noteQrLogin, writeSession } from './session';

export async function createPairingSession() {
  const id = await getIdentity();
  const sessionId = `${id.deviceId}_${Date.now()}`;
  await setDoc(doc(db, 'web_auth_sessions', sessionId), {
    status: 'pending',
    deviceId: id.deviceId,
    publicKey: id.publicKey,
    platform: 'web',
    createdAt: serverTimestamp(),
    userAgent: (navigator.userAgent || '').slice(0, 160),
  });
  /* 🔐 THE CODE NAMES THIS BROWSER'S KEY: `web-login:<sessionId>:<fingerprint>`. The phone reads
     the key from the session doc above and goes no further unless its fingerprint is this one —
     anybody can rewrite a session doc whose id is on the screen; nobody can rewrite what the
     camera saw (services/pairingKey). */
  const qrPayload = webLoginPayload(sessionId, id.publicKey);
  return { sessionId, qrPayload };
}

/** Watch the session for the phone's response; resolves once signed in. */
export function watchPairingSession(sessionId, { onStatus } = {}) {
  return onSnapshot(doc(db, 'web_auth_sessions', sessionId), async (snap) => {
    if (!snap.exists()) return;
    const data = snap.data();
    if (data.status === 'scanned') onStatus?.('scanned');
    /* 🔐 THE TOKEN ARRIVES SEALED TO THIS BROWSER'S KEY. It used to sit in this document in the
       clear, and the document is readable by anyone who knows its id — the id in the code on the
       screen. Whoever photographed the code could sign in as the person who scanned it. The phone
       now boxes the token to the key the code named; only this browser can open it. */
    let token = null;
    if (data.sealedToken && data.tokenNonce && data.sealerPub) {
      try {
        const me = await getIdentity();
        token = decryptText(String(data.sealedToken), String(data.tokenNonce), String(data.sealerPub), me.secretKeyU8);
      } catch { token = null; }
      if (!token) { onStatus?.('error', new Error('sealed token did not open')); return; }
    }
    if (token) {
      try {
        onStatus?.('authenticating');
        // Tell the approval gate this sign-in was approved by a human with the phone in
        // their hand — see noteQrLogin(). Must be set BEFORE the sign-in call, because
        // signInWithCustomToken fires the auth listener synchronously after it resolves.
        noteQrLogin();
        const cred = await signInWithCustomToken(auth, token);
        /* 🔐 the box was sealed by a device OF THIS ACCOUNT — or this is not the account the
           person scanning meant to link, and we leave */
        const sealers = await getDeviceKeys(cred.user.uid, true).catch(() => ({}));
        if (!Object.values(sealers || {}).includes(String(data.sealerPub))) {
          await signOut(auth).catch(() => {});
          onStatus?.('error', new Error('sealer is not a device of this account'));
          return;
        }
        // Self-register the web device under the now-known uid (idempotent, merge).
        await registerDevice(cred.user.uid);
        // 🟢 And put this browser in the account's Active Sessions list, so it can be
        //    seen — and logged out — from the phone like any other device.
        await writeSession(cred.user.uid, { trusted: true, method: 'qr' }).catch(() => {});
        onStatus?.('linked');
        // Session doc is single-use — remove it.
        deleteDoc(doc(db, 'web_auth_sessions', sessionId)).catch(() => {});
      } catch (e) {
        onStatus?.('error', e);
      }
    }
  });
}
